const RESULTS_ROLE = {
    BASE: "base",
    PACKAGE: "package",
    REFERENCE_HOUSE: "referenceHouse",
};

const compareText = (left = "", right = "") => String(left).localeCompare(String(right));

const resultSortKey = (entry = {}) =>
    `${entry.data?.metadata?.filename || ""}\0${entry.upgradePackageKey || ""}`;

const isCliIdMap = (cliId) => cliId && typeof cliId === "object" && !Array.isArray(cliId);

/**
 * Map getCliResults documents into packageResults.
 * Explicit roles never let a package document overwrite base.
 * Legacy documents keep the old shared-base behaviour, but the chosen base
 * document is stable regardless of response order.
 */
export const assemblePackageResults = (resultDocuments = []) => {
    const entries = resultDocuments.filter(Boolean).map((data) => {
        const { metadata: { resultsRole = "", upgradePackageKey = "" } = {} } = data || {};

        return {
            data,
            resultsRole,
            upgradePackageKey,
        };
    });

    const explicitBases = entries
        .filter((entry) => entry.resultsRole === RESULTS_ROLE.BASE)
        .sort((left, right) => compareText(resultSortKey(left), resultSortKey(right)));
    const hasExplicitRole = entries.some((entry) => entry.resultsRole);
    const packageResults = {};

    if (explicitBases[0]) {
        packageResults.base = explicitBases[0].data;
    }

    entries.forEach((entry) => {
        if (entry.resultsRole === RESULTS_ROLE.BASE) {
            return;
        }

        if (entry.resultsRole === RESULTS_ROLE.PACKAGE || entry.resultsRole === RESULTS_ROLE.REFERENCE_HOUSE) {
            if (entry.upgradePackageKey) {
                packageResults[entry.upgradePackageKey] = entry.data;
            }
            return;
        }

        if (entry.upgradePackageKey) {
            packageResults[entry.upgradePackageKey] = entry.data;
        }
    });

    if (!packageResults.base && !hasExplicitRole) {
        const legacyBaseDocuments = entries
            .filter((entry) => !entry.resultsRole && !entry.upgradePackageKey)
            .sort((left, right) => compareText(resultSortKey(left), resultSortKey(right)));

        if (legacyBaseDocuments[0]) {
            packageResults.base = legacyBaseDocuments[0].data;
        } else {
            const legacyPackageDocuments = entries
                .filter((entry) => !entry.resultsRole && entry.upgradePackageKey)
                .sort((left, right) => compareText(left.upgradePackageKey, right.upgradePackageKey));

            if (legacyPackageDocuments[0]) {
                packageResults.base = legacyPackageDocuments[0].data;
            }
        }
    }

    return packageResults;
};

const cliEntries = (cliId = {}) =>
    Object.entries(isCliIdMap(cliId) ? cliId : {}).filter(([, value]) => value?.runId);

/**
 * Run id for the evaluated house.
 * Prefer the dedicated base run, then legacy "default", then any non-reference run.
 */
export const getBaseRunId = (cliId = {}) => {
    if (cliId?.base?.runId) {
        return cliId.base.runId;
    }

    if (cliId?.default?.runId) {
        return cliId.default.runId;
    }

    const entries = cliEntries(cliId);
    const nonReference = entries.filter(([, value]) => value?.resultsRole !== RESULTS_ROLE.REFERENCE_HOUSE);

    const pool = (nonReference.length > 0 ? nonReference : entries).sort(([leftKey], [rightKey]) =>
        compareText(leftKey, rightKey)
    );

    return pool[0]?.[1]?.runId || "";
};

/**
 * Run id for a selected column. Package columns use their own run.
 * The base column, and any column without a run, use the evaluated-house run.
 */
export const getPackageRunId = (cliId = {}, packageKey = "") => {
    if (packageKey && packageKey !== "base" && cliId?.[packageKey]?.runId) {
        return cliId[packageKey].runId;
    }

    return getBaseRunId(cliId);
};

export const getBatchSummaryBaseKey = (cliId = {}, resultsSummary = {}) => {
    if (resultsSummary?.base) {
        return "base";
    }

    if (resultsSummary?.default) {
        return "default";
    }

    const summaryKeys = Object.keys(resultsSummary || {});
    const nonReferenceKeys = summaryKeys
        .filter((key) => cliId?.[key]?.resultsRole !== RESULTS_ROLE.REFERENCE_HOUSE)
        .sort(compareText);

    return nonReferenceKeys[0] || [...summaryKeys].sort(compareText)[0] || "";
};

export const getBatchSummaryUpgradeKeys = (cliId = {}) => {
    if (!isCliIdMap(cliId)) {
        return [];
    }

    if (Object.prototype.hasOwnProperty.call(cliId, "default") && !cliId.base) {
        return [];
    }

    return Object.keys(cliId)
        .filter(
            (key) =>
                key !== "base" &&
                key !== "default" &&
                cliId[key]?.resultsRole !== RESULTS_ROLE.BASE
        )
        .sort(compareText);
};
